import { ReactElement, cloneElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { reduce } from 'ramda'

import { SideNavigationItem, Props as SideNavigationItemProps, isSideNavigationItem } from './side-navigation-item'
import { SideNavigationItemHidden } from './side-navigation-item-hidden'

import styles from './restrict-by-capacity.module.scss';


const MIN_CAPASITY = 3

type Props = {
  capacity: number
  status: 'wide' | 'narrow'
  children:
  | ReactElement<SideNavigationItemProps, typeof SideNavigationItem>
  | Array<ReactElement<SideNavigationItemProps, typeof SideNavigationItem>>
}

export const RestrictByCapacity = ({ capacity: capacityOriginal, status, children }: Props) => {
  const [scrollPosition, setScrollPosition] = useState(0)

  const onlyNavigationItems = useMemo(() => (
    children instanceof Array
      ? children.filter(isSideNavigationItem)
      : [children]
  ), [children])

  const capacity = capacityOriginal < MIN_CAPASITY ? MIN_CAPASITY : capacityOriginal
  const currentNumber = onlyNavigationItems.length
  const showScrollbars = currentNumber > capacity

  const handleScrollFrame = useCallback((values: {
    top: number
    scrollHeight: number
    clientHeight: number
    clientWidth: number
    left: number
    scrollLeft: number
    scrollTop: number
    scrollWidth: number
  }) => {
    const { scrollTop, clientHeight } = values
    const itemHeight = clientHeight / capacity
    setScrollPosition(Math.floor(scrollTop / itemHeight))
  }, [capacity])

  useEffect(() => {
    setScrollPosition(0)
  }, [capacity])

  const childrenList = useMemo(() => {
    let index = 0
    return (
      reduce((acc, child) => {
        if (index >= scrollPosition && index < scrollPosition + capacity) {
          acc.push(cloneElement(child, { collapsed: status === 'narrow' }))
        }
        index += 1
        return acc
      }, [] as Array<ReactElement>, onlyNavigationItems)
    )
  }, [onlyNavigationItems, capacity, status, scrollPosition])

  return (
    <div className={styles.root}>
      {showScrollbars
        && <Scrollbars className={styles.scrollbars} onScrollFrame={handleScrollFrame}>
          {onlyNavigationItems.map(child => (
            <SideNavigationItemHidden key={child.key} />
          ))}
        </Scrollbars>}

      {childrenList}
    </div>
  )
}
